import clsx from 'clsx';
import React from 'react';
import { HeadingContext } from 'react-aria-components';

import { ButtonSkeleton } from '../Button';
import { ListSkeleton } from '../List';
import { TextSkeleton } from '../Skeleton';

/**
 * Indicate the level of the labeled container.
 * 1 is the highest level, a primary container.
 * 2 is the lowest level, a secondary container.
 *
 * @default 1
 */
export type LabeledContainerLevel = 1 | 2;

const LabeledContainerLevelContext = React.createContext<LabeledContainerLevel>(2);

const levelToHeading: Record<LabeledContainerLevel, { level: number; className: string }> = {
    1: { level: 2, className: 'text-ui-heading-small' },
    2: { level: 3, className: 'text-muted text-ui-small' },
};

/**
 * Section container labeled.
 * Do not use it around form elements, and instead use `FieldGroup` for it.
 *
 * @example
 * <LabeledContainer>
 *   <LabeledContainerHeader>
 *     <LabeledContainerActionableTitle>
 *       <Heading>Label</Heading>
 *       <Button label="Action" />
 *     </LabeledContainerActionableTitle>
 *   </LabeledContainerHeader>
 *   <LabeledContainerBody>
 *     <div>Content</div>
 *   </LabeledContainerBody>
 * </LabeledContainer>
 */
export function LabeledContainer(props: {
    children: React.ReactNode;
    className?: string;
    level?: LabeledContainerLevel;
}) {
    const { children, className, level = 1 } = props;

    return (
        <div className={clsx('flex flex-col', className, level > 1 ? 'gap-1.5' : 'gap-2')}>
            <LabeledContainerLevelContext.Provider value={level}>
                <HeadingContext.Provider value={levelToHeading[level]}>
                    {children}
                </HeadingContext.Provider>
            </LabeledContainerLevelContext.Provider>
        </div>
    );
}

export function LabeledContainerHeader(props: { children: React.ReactNode; className?: string }) {
    const { children, className } = props;

    return <div className={clsx('flex flex-col gap-0', className)}>{children}</div>;
}

/**
 * Wrap around the label of the section and an action button.
 * @example
 * <LabeledContainerActionableTitle>
 *   <Heading>Label</Heading>
 *   <Button label="Action" />
 * </LabeledContainerActionableTitle>
 */
export function LabeledContainerActionableTitle(props: {
    children: React.ReactNode;
    className?: string;
}) {
    const { children, className } = props;

    return (
        <div className={clsx('flex flex-row items-center justify-between gap-2', className)}>
            {children}
        </div>
    );
}

export function LabeledContainerBody(props: { children: React.ReactNode; className?: string }) {
    const { children, className } = props;

    return <div className={clsx('flex flex-col gap-2', className)}>{children}</div>;
}

/**
 * Skeleton for a labeled container.
 */
export function LabeledContainerSkeleton(props: { className?: string }) {
    const { className } = props;

    return (
        <LabeledContainer className={className}>
            <LabeledContainerHeader>
                <LabeledContainerActionableTitle>
                    <TextSkeleton>Label</TextSkeleton>
                    <ButtonSkeleton label="Action" iconOnly />
                </LabeledContainerActionableTitle>
            </LabeledContainerHeader>
            <LabeledContainerBody>
                <ListSkeleton items={2} compact />
            </LabeledContainerBody>
        </LabeledContainer>
    );
}
